import React, { useMemo, useEffect, useState } from 'react';
import { DonutChart } from '@/features/journal/components/charts/DonutChart';
import { get } from '@/features/shared/lib/api';

/**
 * CollaborationDonut
 * - Donut chart for "Collaboration" box in ResearcherMetrics.
 * - Props: items = [{ label, value, muted? }], scholarId for API call
 * - Matches the provided design: purple palette, labels and percentages as in the image.
 */
export default function CollaborationDonut({ items: externalItems = null, scholarId = null }) {
  // API에서 데이터 가져오기 (externalItems가 없을 때만)
  const [collaborationData, setCollaborationData] = useState(null);
  const [collaborationLoading, setCollaborationLoading] = useState(false);
  const [collaborationErr, setCollaborationErr] = useState(null);

  useEffect(() => {
    // externalItems가 제공되면 API 호출하지 않음
    if (externalItems !== null) {
      return;
    }

    // scholarId가 없으면 API 호출하지 않음
    if (!scholarId) {
      return;
    }

    let alive = true;
    async function fetchCollaboration() {
      setCollaborationLoading(true);
      setCollaborationErr(null);
      try {
        const response = await get(`/rm/v1/researcher/${encodeURIComponent(scholarId)}/collaboration`);
        if (alive) {
          if (response?.status && response?.result) {
            setCollaborationData(response.result);
          } else {
            setCollaborationErr(response?.message || '데이터를 가져오는데 실패했습니다.');
            setCollaborationData(null);
          }
        }
      } catch (e) {
        if (alive) {
          setCollaborationErr(e?.response?.data?.message || e.message);
        }
      } finally {
        if (alive) {
          setCollaborationLoading(false);
        }
      }
    }
    fetchCollaboration();
    return () => { alive = false; };
  }, [externalItems, scholarId]);

  // 데이터 처리: externalItems 또는 API 데이터 사용
  const items = useMemo(() => {
    if (externalItems !== null && Array.isArray(externalItems)) {
      return externalItems;
    }
    if (collaborationData && Array.isArray(collaborationData)) {
      return collaborationData;
    }
    return null;
  }, [externalItems, collaborationData]);
  // Purple palette (dark to light) to match the mockup
  const palette = ['#2D23D7', '#5E4FF2', '#9B8CFF', '#D5CFFF', '#E4E8F1'];

  // value(호 크기) = count, percent(표시용) = API의 value 그대로 (반올림 없음)
  const data = useMemo(() => {
    const src = Array.isArray(items) && items.length ? items : [];
    return src.map((d, i) => ({
      label: d.label,
      value: Number(d.count) ?? 0,
      percent: Number(d.value) ?? 0,
      muted: !!d.muted,
      color: palette[i] || palette[palette.length - 1],
    }));
  }, [items]);

  const formatPercent = (v) => {
    const n = Number(v);
    if (v == null || v === '' || isNaN(n)) return '0';
    return n % 1 === 0 ? String(n) : n.toFixed(2).replace(/\.?0+$/, '');
  };

  // 로딩 상태 처리
  if (externalItems === null && collaborationLoading) {
    return (
      <div className="flex items-center justify-center min-h-[240px] text-[#8D9AAF]">
        Loading...
      </div>
    );
  }

  // 에러 상태 처리
  if (externalItems === null && collaborationErr) {
    return (
      <div className="flex items-center justify-center min-h-[240px] text-[#F3652D] text-sm">
        {collaborationErr}
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-4 min-[900px]:flex-row min-[900px]:items-center min-[900px]:justify-between">
      {/* Donut on the left */}
      <div className="w-full max-w-[180px] min-[900px]:max-w-[200px]">
        <DonutChart title="" data={data} colors={data.map(d => d.color)} size={140} />
      </div>

      {/* Legend on the right (vertical) */}
      <div className="w-full min-[900px]:w-auto min-[900px]:ml-auto space-y-3 text-[14px] font-semibold text-[#6C7687]">
        {data.map((d, idx) => (
          <div key={idx} className="flex items-center justify-between gap-6">
            <div className="flex items-center gap-2 min-w-0">
              <span className="inline-block w-[10px] h-[10px] rounded-full" style={{ background: d.color, opacity: d.muted ? 0.4 : 1 }} />
              <span className="truncate" title={d.label}>{d.label}</span>
            </div>
            <span className="text-[#6C7687] shrink-0 ml-2">
              {formatPercent(d.percent)}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
